import { ClientInfoStatus } from '@common/interfaces/client'
import moment from 'moment'
import { Category } from '../../interfaces/bbc'
import { ClientIndustries } from '../../interfaces/client'
import { OVERPAYABLE_INTERVAL_VALUES, VENDOR_TYPE_VALUES } from '../client'
import { FilterFieldType, IFilter } from './types'

export const ENTITY_SETTINGS_AR_TABLE_FILTERS_CONFIG = [
  {
    field: 'industry',
    type: FilterFieldType.List,
    title: 'Industry',
    isSortable: true,
    isMultiple: true,
    options: Object.values(ClientIndustries).map((value) => ({
      value,
      label: value,
    })),
    excludeColumn: true,
  },
  {
    field: 'client',
    dbField: 'client_name',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Client',
    title: 'Client',
  },
  {
    field: 'eligible',
    title: 'Eligible',
    type: FilterFieldType.List,
    isSortable: true,
    options: [
      {
        value: true,
        label: 'Eligible',
      },
      {
        value: false,
        label: 'Ineligible',
      },
    ],
  },
  {
    field: 'category',
    type: FilterFieldType.List,
    isSortable: true,
    options: [
      {
        value: 'all',
        label: 'All',
      },
      ...Object.keys(Category).map((category) => ({
        value: category,
        label: category,
      })),
    ],
    title: 'Status',
  },
  {
    field: 'currentAmount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Current Amount',
    align: 'left',
  },
  {
    field: 'lastAmount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Last Amount',
    align: 'left',
  },

  {
    field: 'creditLimit',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Credit limit',
    align: 'left',
  },

  {
    field: 'concentrationLimit',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Conc. limit',
    align: 'left',
  },
  {
    field: 'lastActive',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Last active',
    align: 'left',
    isExternalField: true,
  },
  {
    field: 'clientStatus',
    type: FilterFieldType.List,
    isSortable: true,
    title: 'Client Status',
    options: Object.values(ClientInfoStatus)
      .filter((value) => value !== ClientInfoStatus.Active)
      .map((value) => ({
        value,
        label: value,
      })),
    excludeColumn: true,
  },
] as const satisfies IFilter[]

export const ENTITY_SETTINGS_AP_TABLE_FILTERS_CONFIG = [
  {
    field: 'industry',
    type: FilterFieldType.List,
    title: 'Industry',
    isSortable: true,
    isMultiple: true,
    options: Object.values(ClientIndustries).map((value) => ({
      value,
      label: value,
    })),
    excludeColumn: true,
  },
  {
    field: 'client',
    dbField: 'client_name',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Client',
    title: 'Client',
  },
  {
    field: 'type',
    type: FilterFieldType.List,
    title: 'Type',
    isSortable: true,
    placeholder: 'Type',
    options: VENDOR_TYPE_VALUES,
  },
  {
    field: 'priority',
    type: FilterFieldType.Empty,
    isSortable: false,
    placeholder: 'Priority',
    title: 'Priority',
  },
  {
    field: 'currentAmount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Current Amount',
    align: 'left',
  },
  {
    field: 'lastAmount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Last Amount',
    align: 'left',
  },

  {
    field: 'priorityPayable',
    type: FilterFieldType.List,
    isSortable: true,
    options: OVERPAYABLE_INTERVAL_VALUES,
    title: 'Aging threshold',
  },

  {
    field: 'lastActive',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Last active',
    align: 'left',
    isExternalField: true,
  },
  {
    field: 'clientStatus',
    type: FilterFieldType.List,
    isSortable: true,
    title: 'Client Status',
    options: Object.values(ClientInfoStatus)
      .filter((value) => value !== ClientInfoStatus.Active)
      .map((value) => ({
        value,
        label: value,
      })),
    excludeColumn: true,
  },
] as const satisfies IFilter[]

export const ENTITY_REPORT_SUMMARY_LIST_FILTERS_CONFIG = [
  {
    field: 'clientName',
    type: FilterFieldType.Autocomplete,
    placeholder: 'Client',
    title: 'Client',
    isSortable: true,
  },
  {
    field: '_1To_30Days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '1-30 Days',
  },
  {
    field: '_31To_60Days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '31-60 Days',
  },
  {
    field: '_61To_90Days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '61-90 Days',
  },
  {
    field: '_91PlusDays',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '91+ Days',
  },
  {
    field: '_91PlusDaysPercent',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: '% 91+ Days',
  },
  {
    field: 'ineligible',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total Ineligible',
  },
  {
    field: 'averageTerms',
    type: FilterFieldType.Number,
    isSortable: true,
    title: 'Average Terms',
  },
  {
    field: 'dso',
    type: FilterFieldType.Number,
    isSortable: true,
    title: 'Days Sales Outstanding',
  },
  {
    field: 'total',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total',
  },
  {
    field: 'percent',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: '% of Total',
    excludeFullSearchFilter: true,
    isCustom: true,
  },
  {
    field: 'totalExposure',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total Exposure',
  },
  {
    title: 'AR Status',
    field: 'category',
    type: FilterFieldType.List,
    isMultiple: true,
    excludeColumn: true,
    alias: 'debtor_client_eligibility',
    options: [],
  },
] as const satisfies IFilter[]

export const ENTITY_REPORT_DETAILS_LIST_FILTERS_CONFIG = [
  {
    field: 'invoice',
    type: FilterFieldType.Autocomplete,
    title: 'Invoice',
    placeholder: 'Invoice',
    isSortable: true,
  },
  {
    field: 'daysPastInvoice',
    type: FilterFieldType.Number,
    title: 'Age',
    placeholder: 'Age',
    isSortable: true,
  },
  {
    field: '_1To_30Days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '1-30 Days',
  },
  {
    field: '_31To_60Days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '31-60 Days',
  },
  {
    field: '_61To_90Days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '61-90 Days',
  },
  {
    field: '_91PlusDays',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '91+ Days',
  },
  {
    field: 'total',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total',
  },
  {
    field: 'percent',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: '% of Total',
    excludeFullSearchFilter: true,
    isCustom: true,
  },
] as const satisfies IFilter[]

export const ENTITY_DILUTION_LIST_FILTERS_CONFIG = [
  {
    field: 'clientName',
    type: FilterFieldType.Autocomplete,
    placeholder: 'Client',
    title: 'Client',
    isSortable: true,
  },

  {
    field: 'priorAr',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Prior AR',
  },
  {
    field: 'totalAr',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Current AR',
  },
  {
    field: 'newInvoices',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'New Invoices',
  },
  {
    field: 'impliedCollections',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Implied Collections',
  },
  {
    field: 'totalCollections',
    type: FilterFieldType.Number,
    isSortable: true,
    title: 'Actual Collections',
  },
  {
    field: 'dilution',
    dbField: 'dilution',
    type: FilterFieldType.Number,
    isSortable: true,
    title: 'Dilution',
  },
  {
    title: 'AR Status',
    field: 'category',
    type: FilterFieldType.List,
    isMultiple: true,
    excludeColumn: true,
    alias: 'debtor_client_eligibility',
    options: [],
  },
] as const satisfies IFilter[]

export const ENTITY_ACTIVITY_LIST_FILTERS_CONFIG = [
  {
    field: 'recordDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Date',
    isExternalField: true,
  },
  {
    field: 'clientName',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    placeholder: 'Client',
    title: 'Client',
  },
  {
    field: 'type',
    type: FilterFieldType.List,
    options: [
      {
        value: '',
        label: 'All',
      },
      {
        value: 'New Invoice',
        label: 'New Invoice',
      },
      {
        value: 'Implied Collection',
        label: 'Implied Collection',
      },
      {
        value: 'Check',
        label: 'Check',
      },
      {
        value: 'Wire',
        label: 'Wire',
      },
    ],
    title: 'Type',
  },
  {
    field: 'invoice',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Invoice',
  },
  {
    field: 'amount',
    type: FilterFieldType.Amount,
    title: 'Amount',
    isSortable: true,
    align: 'right',
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'Today',
        filters: {
          recordDateFrom: moment().format('YYYY-MM-DD'),
          recordDateTo: moment().format('YYYY-MM-DD'),
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const ENTITY_ACCOUNTS_LIST_FILTERS_CONFIG = [
  {
    field: 'accountNumber',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Account',
  },
  {
    field: 'lastSeenDate',
    isSortable: true,
    type: FilterFieldType.Date,
    title: 'Last seen',
    excludeFilter: true,
  },
  {
    field: 'lastMappedDate',
    isSortable: true,
    type: FilterFieldType.Date,
    title: 'Mapped date',
    excludeFilter: true,
  },
  {
    field: 'linkedName',
    type: FilterFieldType.Empty,
    title: 'Primary entity record',
    align: 'left',
  },
] as const satisfies IFilter[]

export const ALIAS_LIST_FILTERS_CONFIG = [
  {
    field: 'aka',
    dbField: 'aka',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Description',
    title: 'Description',
  },
  {
    field: 'linkedName',
    type: FilterFieldType.Empty,
    title: 'Customer',
    align: 'left',
  },
  {
    field: 'prediction',
    type: FilterFieldType.Empty,
    title: 'Prediction',
    align: 'left',
  },
  {
    field: 'confidence',
    dbField: 'confidence',
    isSortable: true,
    type: FilterFieldType.Empty,
    title: 'Confidence',
    align: 'left',
  },
  {
    field: 'totalAmount',
    dbField: 'totalAmount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total Amount',
    excludeFullSearchFilter: true,
    excludeFilter: true,
  },
  {
    field: 'clientName',
    dbField: 'clientName',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: '',
    title: 'Client Name',
    excludeFilter: true,
  },

  {
    field: 'lastMappedDate',
    dbField: 'lastMappedDate',
    isSortable: true,
    type: FilterFieldType.Date,
    title: 'Mapped Date',
    excludeFilter: true,
  },
] as const satisfies IFilter[]

export const CUSTOMER_SUMMARY_LIST_FILTERS_CONFIG = [
  {
    field: 'name',
    dbField: 'name',
    type: FilterFieldType.Autocomplete,
    placeholder: 'Customer',
    title: 'Customer',
    isSortable: true,
  },
  {
    field: 'totalExposure',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total Exposure',
  },
  {
    field: 'totalAr',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total AR',
  },
  {
    field: 'totalCollections',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Collections (L3M)',
  },
  {
    field: 'dilution',
    dbField: 'dilution',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Dilution (L3M)',
  },
  {
    field: 'dso',
    dbField: 'dso',
    type: FilterFieldType.Number,
    isSortable: true,
    title: 'Avg DSO (L3M)',
  },
  {
    field: 'totalClients',
    dbField: 'totalClientsAr',
    type: FilterFieldType.Number,
    isSortable: true,
    title: 'Total clients',
  },
  {
    field: 'type',
    type: FilterFieldType.List,
    isMultiple: true,
    excludeColumn: true,
  },
] as const satisfies IFilter[]

export const VENDOR_SUMMARY_LIST_FILTERS_CONFIG = [
  {
    field: 'name',
    dbField: 'name',
    type: FilterFieldType.Autocomplete,
    placeholder: 'Vendor',
    title: 'Vendor',
    isSortable: true,
  },
  {
    field: 'salesforceType',
    type: FilterFieldType.Autocomplete,
    placeholder: 'Type',
    title: 'Type',
    isSortable: true,
  },
  {
    field: 'salesforceSubType',
    type: FilterFieldType.Autocomplete,
    placeholder: 'Sub-Type',
    title: 'Sub-Type',
    isSortable: true,
  },
  {
    field: 'industries',
    dbField: 'industry',
    type: FilterFieldType.Text,
    placeholder: 'Industries',
    title: 'Industries',
    isSortable: false,
  },
  {
    field: 'totalAp',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total AP',
  },
  {
    field: 'totalClients',
    dbField: 'totalClientsAp',
    type: FilterFieldType.Number,
    isSortable: true,
    title: 'Total clients',
  },
] as const satisfies IFilter[]
